import { BoxGeometry, Vector3 } from 'three';
import { Ability } from './Ability.js';
import { settings } from '../config/settings.js';
import { ParticleShape } from '../particles/ParticleSystem.js';
import { RateEmitter } from '../particles/ParticleEngine.js';
import { ConstructStyle } from '../materials/ConstructMaterial.js';
import { BurstMode } from '../effects/BurstSphere.js';
import { DecalType } from '../effects/GroundDecals.js';
import { getColor } from '../utils/color.js';
import { saturate, Easing } from '../utils/math.js';
import {
  createConstruct, setConstructInstance, commitConstructInstances, syncConstruct,
  syncParticlePalette, emitParticles, lifecycleFade, logicalImpactTime,
  safeTimelineDuration, disposeConstruct
} from './NovelSpellUtils.js';

const _pos = new Vector3();
const _scale = new Vector3();
const _rot = new Vector3();
const _up = new Vector3(0, 1, 0);
const _emit = {};

/** Hinged ground plates fold upward one by one, visually turning a path into cover. */
export class FaultFoldAbility extends Ability {
  constructor(context) { super('faultfold', context); }
  createShaders() {
    this.plateGeometry = new BoxGeometry(1, 1, 1);
    this.plates = createConstruct(this.plateGeometry, ConstructStyle.BLADE, { count: 10, normal: true });
    this.group.add(this.plates.mesh);
    this.centre = new Vector3();
    this.emitter = new RateEmitter();
    this.seed = 0;
  }
  createParticles() {
    this.chips = this.ctx.particles.get('faultfold.chips', { capacity: 2400, shape: ParticleShape.CHIP, additive: false, lit: true, softFade: 0.55 });
    this.chips.uniforms.uGravity.value.set(0, -10, 0);
    this.chips.uniforms.uDrag.value = 0.35;
    this.chips.uniforms.uEndSize.value = 0.55;
  }
  get impactDuration() { return settings.faultfold.lifetime * settings.global.lifetime; }
  get fadeDuration() { return settings.faultfold.fadeTime; }
  get instanceCount() { return this.plates.mesh.count; }
  onSpawn() { this.seed = Math.random() * 10; this.emitter.reset(); this.plates.mesh.visible = false; }
  onTravel(dt) {
    const c = settings.faultfold;
    syncParticlePalette(this.chips, c);
    const count = this.emitter.tick(dt, c.particleRate * 0.35);
    if (count > 0) emitParticles(this.chips, _emit, { position: this.position, direction: _up, count, radius: 0.35, speed: 2.4, spread: 1, size: c.particleSize, life: c.particleLife });
  }
  onImpact() {
    const c = settings.faultfold;
    this.pointAt(0.5, this.centre).setY(0);
    this.position.copy(this.centre).setY(c.foldHeight * 0.45);
    this.lightBoost = c.impactLight;
    this.ctx.bursts.spawn(BurstMode.EARTH, this.centre, { radius: 0.3, endRadius: c.plateWidth * 1.4, life: 0.65, intensity: 0.95, colorA: getColor(c.colorC), colorB: getColor(c.colorB), colorC: getColor(c.colorA) });
    this.ctx.decals.spawn(DecalType.CRACK, this.centre, { radius: Math.min(this.length, c.plates * c.plateLength) * 0.5, life: c.lifetime + c.fadeTime, intensity: 0.85, colorA: getColor(c.colorB), colorB: getColor(c.colorC) });
    this.ctx.shake.add(c.impactShake * settings.global.cameraShake, c.shakeDuration);
    this.ctx.flash.trigger(getColor(c.colorC), c.impactFlash);
  }
  onFade(dt, t) {
    const c = settings.faultfold;
    const fade = lifecycleFade(t);
    const time = logicalImpactTime(this.impactTime);
    const foldTime = safeTimelineDuration(c.foldTime);
    const plates = Math.min(10, Math.max(1, Math.round(c.plates)));
    const yaw = Math.atan2(this.direction.x, this.direction.z);
    for (let i = 0; i < plates; i++) {
      const q = plates <= 1 ? 0.5 : i / (plates - 1);
      const local = Easing.outBack(saturate((time - i * c.hingeStagger) / foldTime));
      _pos.copy(this.origin).addScaledVector(this.direction, q * this.length).setY(c.foldHeight * local * 0.5 * fade);
      _scale.set(c.plateWidth, Math.max(0.05, c.foldHeight * local * fade), c.plateLength * 0.82);
      _rot.set(0, yaw + (i & 1 ? 0.08 : -0.08), (i & 1 ? -1 : 1) * (1 - local) * 0.72);
      setConstructInstance(this.plates.mesh, i, _pos, _scale, _rot);
    }
    commitConstructInstances(this.plates.mesh, plates);
    syncConstruct(this.plates, c, { seed: this.seed, reveal: saturate(time / foldTime), fade, pulse: 0.25 });
    syncParticlePalette(this.chips, c);
    const count = this.emitter.tick(dt, c.particleRate);
    if (count > 0) emitParticles(this.chips, _emit, { position: _pos, direction: _up, count, radius: c.plateWidth, speed: 4.5, spread: 1, size: c.particleSize, life: c.particleLife });
  }
  onDestroy() { this.plates.mesh.visible = false; }
  dispose() { disposeConstruct(this.plates); this.plateGeometry.dispose(); super.dispose(); }
}
